import React, { useEffect, useRef } from "react";
import { useAuth } from "../../hooks/useAuth";
import { noteSignedIn, pageHadSession } from "../../services/session";

interface ProtectedRouteProps {
  children: React.ReactNode;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { isAuthenticated, isLoading, login, error, user } = useAuth();
  const loginInitiatedRef = useRef(false);

  // Check if we're in the middle of processing a callback
  const isCallback =
    window.location.pathname === "/auth/callback" ||
    window.location.search.includes("code=") ||
    window.location.search.includes("state=");

  useEffect(() => {
    console.log("ProtectedRoute - Auth State Changed:", {
      isAuthenticated,
      isLoading,
      hasUser: !!user,
      error: error?.message,
      currentURL: window.location.href,
      hasAuthCode: window.location.search.includes("code="),
      isCallback,
      loginInitiated: loginInitiatedRef.current,
    });
  }, [isAuthenticated, isLoading, user, error, isCallback]);

  // A session that ended on a page already in use keeps the page on screen: the sign-in dialog
  // (`SessionEndedDialog`) asks for a new sign-in over it, so nothing typed is lost. Only a
  // first visit is sent to Keycloak from here.
  if (isAuthenticated && user) noteSignedIn(user);
  const keepPage = !isCallback && pageHadSession();

  // Trigger login if not authenticated and not processing callback
  useEffect(() => {
    if (!isAuthenticated && !isLoading && !error && !isCallback && !keepPage && !loginInitiatedRef.current) {
      console.log("🔒 Initiating login process");
      loginInitiatedRef.current = true;
      login();
    }
    
    // Reset login initiated flag when user becomes authenticated
    if (isAuthenticated && loginInitiatedRef.current) {
      loginInitiatedRef.current = false;
    }
  }, [isAuthenticated, isLoading, error, isCallback, keepPage, login]);

  if (keepPage && (!isAuthenticated || error)) return <>{children}</>;

  // Show loading while authentication is being determined
  if (isLoading) {
    return (
      <div className="auth-callback-container">
        <div className="auth-content">
          <div className="auth-spinner"></div>
          <h2 className="auth-title">Authenticating</h2>
          <p className="auth-message">Verifying your credentials...</p>
        </div>
      </div>
    );
  }

  // Show error if authentication failed
  if (error) {
    return (
      <div className="auth-callback-container">
        <div className="auth-content">
          <div className="auth-error-icon">⚠️</div>
          <h2 className="auth-title error">Authentication Error</h2>
          <p className="auth-message">There was an error during authentication:</p>
          <p className="auth-error-details">{error.message}</p>
          <button onClick={login} className="auth-retry-button">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // Show loading while not authenticated
  if (!isAuthenticated) {
    console.log("🔒 User not authenticated, showing auth loading screen");
    return (
      <div className="auth-callback-container">
        <div className="auth-content">
          <div className="auth-spinner"></div>
          <h2 className="auth-title">
            {isCallback ? "Processing Authentication" : "Redirecting to Login"}
          </h2>
          <p className="auth-message">
            {isCallback
              ? "Please wait while we process your authentication..."
              : "Redirecting to secure login..."}
          </p>
        </div>
      </div>
    );
  }

  console.log("✅ User authenticated, rendering protected content");
  return <>{children}</>;
};

export default ProtectedRoute;
